import { ListWildCardsQuery, ListWildCardsQueryVariables, WildcardEntity } from '@/graphql/generated/graphql';
import { FETCH_WILDCARDS } from '@/graphql/queries/wildcard/wildcard';
import { useQuery, useMutation } from '@apollo/client';
import { useAnnouncementStore } from '@/pages/admin/announcements/store/announcementStore';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useState } from 'react';
import { DELETE_WILDCARDS, PUBLISH_WILDCARDS, UNPUBLISH_WILDCARDS } from '@/graphql/mutations/wildcard/wildcard';
import { useToast } from '@/components/ui/use-toast';

// Configure dayjs to use timezone
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('Europe/London');

export const useAnnouncementsList = () => {
  const { data, refetch } = useQuery<ListWildCardsQuery, ListWildCardsQueryVariables>(FETCH_WILDCARDS);
  const { setEditingAnnouncement, page } = useAnnouncementStore();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedWildcardId, setSelectedWildcardId] = useState<string | null>(null);
  const { toast } = useToast();

  const [publishWildcard] = useMutation(PUBLISH_WILDCARDS, {
    onCompleted: () => refetch(),
  });

  const [unpublishWildcard] = useMutation(UNPUBLISH_WILDCARDS, {
    onCompleted: () => refetch(),
  });

  const [deleteWildcard] = useMutation(DELETE_WILDCARDS, {
    onCompleted: () => refetch(),
  });

  const handlePublish = (id: string) => {
    publishWildcard({
      variables: { id },
      onCompleted: () => {
        refetch();
        toast({
          title: page?.preview?.publishSuccess || '',
          description: page?.preview?.publishSuccessDescription || '',
        });
      },
      onError: () => {
        toast({
          title: page?.preview?.publishError || '',
          description: page?.preview?.publishErrorDescription || '',
          variant: 'destructive',
        });
      },
    });
  };

  const handleUnpublish = (id: string) => {
    unpublishWildcard({
      variables: { id },
      onCompleted: () => {
        refetch();
        toast({
          title: page?.preview?.unpublishSuccess || '',
          description: page?.preview?.unpublishSuccessDescription || '',
        });
      },
      onError: () => {
        toast({
          title: page?.preview?.unpublishError || '',
          description: page?.preview?.unpublishErrorDescription || '',
          variant: 'destructive',
        });
      },
    });
  };

  const handleDelete = (id: string) => {
    setSelectedWildcardId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedWildcardId) {
      deleteWildcard({
        variables: { id: selectedWildcardId },
        onCompleted: () => {
          refetch();
          toast({
            title: page?.preview?.deleteSuccess || '',
            description: page?.preview?.deleteSuccessDescription || '',
          });
        },
        onError: () => {
          toast({
            title: page?.preview?.deleteError || '',
            description: page?.preview?.deleteErrorDescription || '',
            variant: 'destructive',
          });
        },
      });
      setDeleteDialogOpen(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setSelectedWildcardId(null);
  };

  const handleDuplicateAndRelaunch = (wildcard: WildcardEntity) => {
    const imageUrl = wildcard.attributes?.image?.data?.attributes?.url;
    const fileName = imageUrl?.split('/').pop() || 'logo.png';
    const file = new File([], fileName, { type: 'image/png' });
    const [startYear, startMonth, startDay] = wildcard.attributes?.startDate?.split('-') || [];
    const [endYear, endMonth, endDay] = wildcard.attributes?.endDate?.split('-') || [];

    // Set form data with duplicated values
    setEditingAnnouncement(null, {
      startDate: new Date(startYear, startMonth - 1, startDay),
      endDate: new Date(endYear, endMonth - 1, endDay),
      description: wildcard.attributes?.description || '',
      logo: file,
      logoId: wildcard.attributes?.image?.data?.id || '',
      schoolIds: wildcard.attributes?.schools?.data.map((school) => school.id!) || [],
    });

    // Scroll to the form
    window.scrollTo({ top: 200, behavior: 'smooth' });
  };

  const handleEdit = (wildcard: WildcardEntity) => {
    const imageUrl = wildcard.attributes?.image?.data?.attributes?.url;
    const fileName = imageUrl?.split('/').pop() || 'logo.png';
    const file = new File([], fileName, { type: 'image/png' });
    const [startYear, startMonth, startDay] = wildcard.attributes?.startDate?.split('-') || [];
    const [endYear, endMonth, endDay] = wildcard.attributes?.endDate?.split('-') || [];
    setEditingAnnouncement(wildcard.id!, {
      startDate: new Date(startYear, startMonth - 1, startDay),
      endDate: new Date(endYear, endMonth - 1, endDay),
      description: wildcard.attributes?.description || '',
      logo: file,
      logoId: wildcard.attributes?.image?.data?.id || '',
      schoolIds: wildcard.attributes?.schools?.data.map((school) => school.id!) || [],
    });
    window.scrollTo({ top: 200, behavior: 'smooth' });
  };

  return {
    data,
    deleteDialogOpen,
    handleUnpublish,
    handlePublish,
    handleDelete,
    handleDuplicateAndRelaunch,
    handleEdit,
    setEditingAnnouncement,
    setDeleteDialogOpen,
    confirmDelete,
    cancelDelete,
  };
};
